import { Badge, Modal, Tag } from 'antd';
import React, { Fragment, useState } from 'react';
import { SuccessManagerIcon, TrialIcon, downloadIcon } from './Icons';
import { useSelector } from 'react-redux';
import { getKhataCredits } from '../../store/selectors/session';
import { YEAR_END_REVIEW } from '../../api/constants';
import { getDoc, getMe } from '../../store/idbHandler';
import { getAclViewClassName } from '../../restrictions/utils';
import { SREV2 } from '../../api/pushGenie';

const onShare = (message: string): void => {
    try {
        window.open(`https://api.whatsapp.com/send?text=${message}`, '_blank');
    } catch (e) {
        console.error(e);
    }
};

const onCopy = (message: string): void => {
    try {
        const el = document.createElement('textarea');
        el.value = message;
        document.body.appendChild(el);
        el.select();
        document.execCommand('copy');
        document.body.removeChild(el);
    } catch (e) {
        console.error(e);
    }
};

export const ImageComponent: React.FC<{ imgUrl: string; className?: string }> = ({
    imgUrl,
    className,
}) => {
    const me = getMe();
    return (
        <div
            className={
                className ??
                'flex items-start justify-center min-w-64 min-h-64 w-64 h-64 self-start'
            }
        >
            {me?.loginmeta?.role && me?.loginmeta?.role === 'STAFF' ? (
                <Badge
                    count={
                        <p
                            className="p-5 text-10 font-700 rounded-3"
                            style={{ background: '#febc2b' }}
                        >
                            {me?.loginmeta?.role}
                        </p>
                    }
                    offset={[-32, 64]}
                >
                    <img className="w-64 h-64 rounded-full" src={imgUrl} />
                </Badge>
            ) : (
                <img className="w-64 h-64 rounded-full" src={imgUrl} />
            )}
        </div>
    );
};

export const NameComponent: React.FC<{
    dName: string;
    newDesign?: boolean;
}> = ({ dName, newDesign = false }) => {
    const doc = getMe()?.doctor;
    return (
        <Fragment>
            <div className="flex items-center space-x-4">
                <p className="text-neutral-1 font-700 capitalize">{dName}</p>
                <div className="flex items-center justify-center w-16 h-16">
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 512 512"
                        width="16"
                        height="16"
                        className={
                            newDesign
                                ? 'fill-darwin-accent-bright-blue-800'
                                : 'fill-actionSecondary'
                        }
                    >
                        <path d="M512 256c0-37.7-23.7-69.9-57.1-82.4 14.7-32.4 8.8-71.9-17.9-98.6-26.7-26.7-66.2-32.6-98.6-17.9C325.9 23.7 293.7 0 256 0s-69.9 23.7-82.4 57.1c-32.4-14.7-72-8.8-98.6 17.9-26.7 26.7-32.6 66.2-17.9 98.6C23.7 186.1 0 218.3 0 256s23.7 69.9 57.1 82.4c-14.7 32.4-8.8 72 17.9 98.6 26.6 26.6 66.1 32.7 98.6 17.9 12.5 33.3 44.7 57.1 82.4 57.1s69.9-23.7 82.4-57.1c32.6 14.8 72 8.7 98.6-17.9 26.7-26.7 32.6-66.2 17.9-98.6 33.4-12.5 57.1-44.7 57.1-82.4zm-144.8-44.25L236.16 341.74c-4.31 4.28-11.28 4.25-15.55-.06l-75.72-76.33c-4.28-4.31-4.25-11.28.06-15.56l26.03-25.82c4.31-4.28 11.28-4.25 15.56.06l42.15 42.49 97.2-96.42c4.31-4.28 11.28-4.25 15.55.06l25.82 26.03c4.28 4.32 4.26 11.29-.06 15.56z" />
                    </svg>
                </div>
            </div>
            {!newDesign && (
                <p className="text-neutral-1 text-13">
                    {doc?.profile.professional.major_speciality?.name}
                </p>
            )}
        </Fragment>
    );
};

export const CopyShareComponent: React.FC<{
    showInviteCode: boolean | '' | undefined;
    showInviteLink: boolean | '' | undefined;
    message: string;
    newDesign?: boolean;
}> = ({ showInviteCode, showInviteLink, message, newDesign = false }) => {
    return (
        <Fragment>
            {showInviteCode && showInviteLink && (
                <div
                    className={`flex items-center mt-10 space-x-16 ${getAclViewClassName(
                        SREV2.INVITE_CODE,
                    )}`}
                >
                    <div
                        onClick={() => onCopy(message)}
                        className="flex items-center space-x-4 cursor-pointer select-all"
                    >
                        <div className="flex items-center justify-center w-16 h-16">
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className={
                                    newDesign
                                        ? 'fill-darwin-accent-bright-blue-800'
                                        : 'fill-actionPrimary'
                                }
                                viewBox="0 0 448 512"
                                width="16"
                                height="16"
                            >
                                <path d="M320 448v40c0 13.255-10.745 24-24 24H24c-13.255 0-24-10.745-24-24V120c0-13.255 10.745-24 24-24h72v296c0 30.879 25.121 56 56 56h168zm0-344V0H152c-13.255 0-24 10.745-24 24v368c0 13.255 10.745 24 24 24h272c13.255 0 24-10.745 24-24V128H344c-13.2 0-24-10.8-24-24zm120.971-31.029L375.029 7.029A24 24 0 0 0 358.059 0H352v96h96v-6.059a24 24 0 0 0-7.029-16.97z" />
                            </svg>
                        </div>
                        <p
                            className={`${
                                newDesign
                                    ? 'text-darwin-accent-bright-blue-800'
                                    : 'text-actionPrimary'
                            } font-700 text-13`}
                        >
                            Copy
                        </p>
                    </div>
                    <div
                        onClick={() => onShare(message)}
                        className="flex items-center space-x-4 cursor-pointer"
                    >
                        <div className="flex items-center justify-center w-16 h-16">
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 512 512"
                                className={
                                    newDesign
                                        ? 'fill-darwin-accent-bright-blue-800'
                                        : 'fill-actionPrimary'
                                }
                                width="16"
                                height="16"
                            >
                                <path d="M503.691 189.836L327.687 37.851C312.281 24.546 288 35.347 288 56.015v80.053C127.371 137.907 0 170.1 0 322.326c0 61.441 39.581 122.309 83.333 154.132 13.653 9.931 33.111-2.533 28.077-18.631C66.066 312.814 132.917 274.316 288 272.085V360c0 20.7 24.3 31.453 39.687 18.164l176.004-152c11.071-9.562 11.086-26.753 0-36.328z" />
                            </svg>
                        </div>
                        <p
                            className={`${
                                newDesign
                                    ? 'text-darwin-accent-bright-blue-800'
                                    : 'text-actionPrimary'
                            } font-700 text-13`}
                        >
                            Share
                        </p>
                    </div>
                </div>
            )}
        </Fragment>
    );
};

const ProfileComponent: React.FC<{
    isNew?: boolean;
    tagValue?: string;
    tagColorClassBG?: string;
    tagColorClassText?: string;
}> = (props) => {
    return (
        <div className="flex items-center gap-8 py-4">
            <div className="flex items-center w-full gap-8">{props.children}</div>
            {props.isNew && (
                <Tag className="self-end ml-auto border-none text-11 bg-darwin-accent-green-profile-tag-100 text-darwin-accent-green-profile-tag-200 font-700 rounded-24 py-2 px-4">
                    New
                </Tag>
            )}
            {props.tagValue && (
                <Tag
                    className={`self-end ml-auto border-none text-11 ${
                        props.tagColorClassBG ?? 'bg-darwin-accent-green-profile-tag-100'
                    } ${
                        props.tagColorClassText ?? 'text-darwin-accent-green-profile-tag-200'
                    } font-700 rounded-24 py-2 px-4`}
                >
                    {props.tagValue}
                </Tag>
            )}
        </div>
    );
};

export const YearReviewComponent: React.FC = () => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    return (
        <>
            <div
                className="py-8 px-16 cursor-pointer"
                style={{ background: 'linear-gradient(100deg, #95E9C1 2.51%, #D8FF71 134.8%)' }}
            >
                <div
                    className="flex items-center"
                    onClick={() => {
                        setIsModalOpen(true);
                    }}
                >
                    <div className="flex items-center w-full gap-8">
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            height="16"
                            width="18"
                            viewBox="0 0 576 512"
                        >
                            <path d="M288 32c-80.8 0-145.5 36.8-192.6 80.6C48.6 156 17.3 208 2.5 243.7c-3.3 7.9-3.3 16.7 0 24.6C17.3 304 48.6 356 95.4 399.4C142.5 443.2 207.2 480 288 480s145.5-36.8 192.6-80.6c46.8-43.5 78.1-95.4 93-131.1c3.3-7.9 3.3-16.7 0-24.6c-14.9-35.7-46.2-87.7-93-131.1C433.5 68.8 368.8 32 288 32zM144 256a144 144 0 1 1 288 0 144 144 0 1 1 -288 0zm144-64c0 35.3-28.7 64-64 64c-7.1 0-13.9-1.2-20.3-3.3c-5.5-1.8-11.9 1.6-11.7 7.4c.3 6.9 1.3 13.8 3.2 20.7c13.7 51.2 66.4 81.6 117.6 67.9s81.6-66.4 67.9-117.6c-11.1-41.5-47.8-69.4-88.6-71.1c-5.8-.2-9.2 6.1-7.4 11.7c2.1 6.4 3.3 13.2 3.3 20.3z"></path>
                        </svg>
                        <p className="text-13 font-700">Your Practice Insights - 2023</p>
                    </div>
                </div>
            </div>
            <Modal
                title={null}
                centered
                visible={isModalOpen}
                onCancel={() => setIsModalOpen(false)}
                destroyOnClose={true}
                closable={false}
                footer={null}
                bodyStyle={{
                    padding: '0px',
                }}
                style={{
                    borderRadius: '8px',
                    overflow: 'hidden',
                }}
                width={300}
            >
                <div className="min-h-592 max-h-592 w-300 h-592 h-full rounded-8">
                    <iframe
                        src={YEAR_END_REVIEW(getDoc()?._id || '')}
                        className="w-300 h-592 min-h-592 max-h-592 h-full rounded-8"
                        referrerPolicy="unsafe-url"
                    ></iframe>
                </div>
            </Modal>
        </>
    );
};

export const MyProfileComponent = (): JSX.Element => {
    return (
        <ProfileComponent>
            <svg
                width="12"
                height="14"
                viewBox="0 0 12 14"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
            >
                <path
                    d="M7.21875 7.84375C9.68164 7.84375 11.6875 9.84961 11.6875 12.3125C11.6875 12.7695 11.3066 13.125 10.875 13.125H1.125C0.667969 13.125 0.3125 12.7695 0.3125 12.3125C0.3125 9.84961 2.29297 7.84375 4.78125 7.84375H7.21875ZM1.53125 11.9062H10.4434C10.2402 10.3066 8.86914 9.0625 7.21875 9.0625H4.78125C3.10547 9.0625 1.73438 10.3066 1.53125 11.9062ZM6 6.625C4.19727 6.625 2.75 5.17773 2.75 3.375C2.75 1.59766 4.19727 0.125 6 0.125C7.77734 0.125 9.25 1.59766 9.25 3.375C9.25 5.17773 7.77734 6.625 6 6.625ZM6 1.34375C4.85742 1.34375 3.96875 2.25781 3.96875 3.375C3.96875 4.51758 4.85742 5.40625 6 5.40625C7.11719 5.40625 8.03125 4.51758 8.03125 3.375C8.03125 2.25781 7.11719 1.34375 6 1.34375Z"
                    fill="#484848"
                />
            </svg>
            <p className="text-13">My Profile</p>
        </ProfileComponent>
    );
};

export const MyGenieComponent = (): JSX.Element => {
    return (
        <ProfileComponent>
            <svg
                width="14"
                height="14"
                viewBox="0 0 14 14"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
            >
                <path
                    d="M7.07617 2.30859C7.02539 2.2832 7 2.23242 7 2.18164C7 2.10547 7.02539 2.05469 7.07617 2.0293L8.3457 1.49609L8.87891 0.226562C8.9043 0.175781 8.95508 0.125 9.03125 0.125C9.08203 0.125 9.13281 0.175781 9.1582 0.226562L9.69141 1.49609L10.9609 2.00391C11.0117 2.05469 11.0371 2.10547 11.0371 2.15625C11.0371 2.23242 11.0117 2.2832 10.9609 2.30859L9.69141 2.8418L9.1582 4.11133C9.13281 4.16211 9.08203 4.21289 9.00586 4.21289C8.95508 4.21289 8.9043 4.16211 8.85352 4.11133L8.3457 2.8418L7.07617 2.30859ZM13.4238 5.71094C13.4492 5.73633 13.5 5.76172 13.5 5.8125C13.5 5.86328 13.4492 5.91406 13.3984 5.93945L12.4082 6.37109L11.9766 7.38672C11.9512 7.41211 11.9004 7.46289 11.8496 7.46289C11.7988 7.46289 11.7734 7.41211 11.748 7.38672L11.3164 6.37109L10.3262 5.93945C10.2754 5.91406 10.2246 5.88867 10.2246 5.83789C10.2246 5.78711 10.2754 5.73633 10.3262 5.71094L11.3164 5.2793L11.748 4.28906C11.7734 4.23828 11.8242 4.21289 11.875 4.21289C11.9258 4.21289 11.9512 4.23828 11.9766 4.28906L12.4082 5.2793L13.4238 5.71094ZM9.74219 6.92969C10.2246 6.98047 10.4023 7.58984 10.0723 7.91992L8.14258 9.79883L8.59961 12.4648C8.625 12.668 8.54883 12.8965 8.3457 13.0234C8.16797 13.1504 7.93945 13.1758 7.73633 13.0742L5.375 11.8047L2.98828 13.0742C2.88672 13.125 2.81055 13.125 2.70898 13.125C2.37891 13.125 2.07422 12.8203 2.15039 12.4648L2.58203 9.79883L0.677734 7.91992C0.322266 7.58984 0.5 6.98047 0.982422 6.92969L3.64844 6.52344L4.8418 4.11133C4.94336 3.9082 5.14648 3.78125 5.34961 3.78125C5.57812 3.78125 5.78125 3.9082 5.88281 4.11133L7.07617 6.52344L9.74219 6.92969ZM6.82227 9.36719L8.29492 7.94531L6.26367 7.64062L5.375 5.8125L4.46094 7.64062L2.42969 7.94531L3.90234 9.36719L3.54688 11.3984L5.375 10.4336L7.17773 11.3984L6.82227 9.36719Z"
                    fill="#484848"
                />
            </svg>
            <p className="text-13">My Genie</p>
        </ProfileComponent>
    );
};

export const DoctorReferralComponent = (): JSX.Element => {
    return (
        <ProfileComponent>
            <svg
                xmlns="http://www.w3.org/2000/svg"
                width="14"
                height="14"
                viewBox="0 0 14 14"
                fill="none"
            >
                <path
                    d="M7 12.4062C8.87891 12.4062 10.6055 11.416 11.5703 9.76562C12.5098 8.14062 12.5098 6.13477 11.5703 4.48438C10.6055 2.85938 8.87891 1.84375 7 1.84375C5.0957 1.84375 3.36914 2.85938 2.4043 4.48438C1.46484 6.13477 1.46484 8.14062 2.4043 9.76562C3.36914 11.416 5.0957 12.4062 7 12.4062ZM7 0.625C9.31055 0.625 11.4434 1.86914 12.6113 3.875C13.7793 5.90625 13.7793 8.36914 12.6113 10.375C11.4434 12.4062 9.31055 13.625 7 13.625C4.66406 13.625 2.53125 12.4062 1.36328 10.375C0.195312 8.36914 0.195312 5.90625 1.36328 3.875C2.53125 1.86914 4.66406 0.625 7 0.625ZM9.4375 8.59766C9.4375 8.90234 9.1582 9.15625 8.85352 9.15625C8.70117 9.15625 8.54883 9.10547 8.44727 9.00391L7.60938 8.14062L5.9082 9.8418C5.83203 9.91797 5.70508 9.96875 5.57812 9.96875C5.42578 9.96875 5.32422 9.91797 5.22266 9.8418L4.2832 8.90234C4.20703 8.80078 4.15625 8.69922 4.15625 8.54688C4.15625 8.41992 4.20703 8.29297 4.2832 8.2168L5.98438 6.51562L5.12109 5.67773C5.01953 5.57617 4.96875 5.42383 4.96875 5.27148C4.96875 4.9668 5.22266 4.6875 5.52734 4.6875H9.03125C9.23438 4.6875 9.4375 4.89062 9.4375 5.09375V8.59766Z"
                    fill="#484848"
                />
            </svg>
            <p className="text-13">My Patient Referrals</p>
        </ProfileComponent>
    );
};

export const MyRXTemplateComponent = (): JSX.Element => {
    return (
        <ProfileComponent>
            <svg
                width="12"
                height="14"
                viewBox="0 0 12 14"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
            >
                <path
                    d="M8.46289 9.0625L11.4844 12.1094C11.7383 12.3379 11.7383 12.7188 11.4844 12.9473C11.3828 13.0742 11.2305 13.125 11.0781 13.125C10.9004 13.125 10.748 13.0742 10.6465 12.9473L7.625 9.92578L4.57812 12.9473C4.47656 13.0742 4.32422 13.125 4.17188 13.125C3.99414 13.125 3.8418 13.0742 3.74023 12.9473C3.48633 12.7188 3.48633 12.3379 3.74023 12.084L6.76172 9.0625L3.51172 5.8125H1.53125V7.64062C1.53125 7.99609 1.20117 8.27539 0.896484 8.27539C0.566406 8.27539 0.3125 7.99609 0.3125 7.66602V0.734375C0.3125 0.404297 0.566406 0.125 0.921875 0.125H4.78125C6.33008 0.125 7.625 1.41992 7.625 2.96875C7.625 4.41602 6.55859 5.58398 5.1875 5.78711L7.625 8.22461L10.6211 5.20312C10.875 4.94922 11.2559 4.94922 11.4844 5.20312C11.7383 5.43164 11.7383 5.8125 11.4844 6.04102L8.46289 9.0625ZM1.53125 1.34375V4.59375H4.78125C5.66992 4.59375 6.40625 3.88281 6.40625 2.96875C6.40625 2.08008 5.66992 1.34375 4.78125 1.34375H1.53125Z"
                    fill="#484848"
                />
            </svg>

            <p className="text-13">My RX Template</p>
        </ProfileComponent>
    );
};

export const MyAvailabilityComponent = (): JSX.Element => {
    return (
        <ProfileComponent>
            <svg
                width="12"
                height="14"
                viewBox="0 0 12 14"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
            >
                <path
                    d="M4.17188 1.75H7.82812V0.734375C7.82812 0.404297 8.08203 0.125 8.4375 0.125C8.76758 0.125 9.04688 0.404297 9.04688 0.734375V1.75H10.0625C10.9512 1.75 11.6875 2.48633 11.6875 3.375V11.5C11.6875 12.4141 10.9512 13.125 10.0625 13.125H1.9375C1.02344 13.125 0.3125 12.4141 0.3125 11.5V3.375C0.3125 2.48633 1.02344 1.75 1.9375 1.75H2.95312V0.734375C2.95312 0.404297 3.20703 0.125 3.5625 0.125C3.89258 0.125 4.17188 0.404297 4.17188 0.734375V1.75ZM1.53125 11.5C1.53125 11.7285 1.70898 11.9062 1.9375 11.9062H10.0625C10.2656 11.9062 10.4688 11.7285 10.4688 11.5V5H1.53125V11.5Z"
                    fill="#484848"
                />
            </svg>

            <p className="text-13">My Availability</p>
        </ProfileComponent>
    );
};

export const SubscribeComponent = (): JSX.Element => {
    return (
        <ProfileComponent>
            <svg
                xmlns="http://www.w3.org/2000/svg"
                width="12"
                height="14"
                viewBox="0 0 12 14"
                fill="none"
            >
                <path
                    d="M11.9971 4.27907C11.9633 4.85013 11.7268 5.3876 11.3043 5.80749L7.24897 9.8385C7.14759 9.93928 7.02931 10.0401 6.89413 10.124C5.96477 10.7455 4.69747 10.6279 3.90329 9.8385C3.41327 9.35142 3.15981 8.66279 3.2274 7.95736C3.2443 7.53747 3.59914 7.21835 4.02157 7.21835C4.46091 7.21835 4.81575 7.57106 4.81575 8.00775C4.81575 8.24289 4.71437 8.44444 4.56229 8.59561C4.61298 8.72997 4.69747 8.86434 4.81575 8.96512C5.01852 9.16667 5.30578 9.28424 5.59303 9.28424C5.7789 9.28424 5.94788 9.25065 6.09995 9.16667C6.20134 9.11628 6.28582 9.0491 6.37031 8.96512L8.3642 6.9832L7.53623 6.16021L6.35341 4.9845C6.15064 4.78294 5.86339 4.66537 5.57613 4.66537C5.28888 4.66537 5.00162 4.78294 4.79885 4.9845C4.66367 5.11886 4.56229 5.28682 4.5116 5.45478C4.69747 5.60594 4.83265 5.82429 4.83265 6.07623C4.83265 6.51292 4.4778 6.86563 4.03847 6.86563C3.63293 6.86563 3.27809 6.54651 3.2443 6.14341C3.10912 5.3876 3.34568 4.63178 3.90329 4.07752C4.34263 3.64083 4.93403 3.38889 5.57613 3.38889C6.20134 3.38889 6.80964 3.64083 7.24897 4.07752L8.43179 5.25323L9.25976 6.07623L10.4088 4.93411C10.6284 4.71576 10.7298 4.44703 10.7298 4.1447C10.7298 3.87597 10.6284 3.62403 10.4426 3.43928C9.95255 2.90181 9.36115 2.48191 8.70215 2.17959C8.00936 1.87726 7.28277 1.7093 6.52239 1.7093C5.10301 1.7093 3.78501 2.26357 2.77117 3.25452C1.77422 4.24548 1.21661 5.57235 1.21661 6.9832C1.21661 8.39406 1.77422 9.70413 2.77117 10.7119C3.76811 11.7028 5.10301 12.2571 6.52239 12.2571C7.28277 12.2571 8.00936 12.1059 8.70215 11.7868C9.36115 11.4845 9.95255 11.0646 10.4426 10.5271C10.8312 10.0904 10.8143 9.4354 10.4088 9.0155L9.56391 8.17571L10.4595 7.28553L11.3043 8.12532C12.1999 9.0155 12.2337 10.4264 11.3888 11.3669C10.7805 12.0388 10.0539 12.5594 9.24286 12.9289C8.39799 13.3152 7.48554 13.5 6.53928 13.5C5.66062 13.5 4.79885 13.332 3.98778 12.9961C3.2105 12.6602 2.50081 12.1899 1.9094 11.6021C1.3011 10.9974 0.827971 10.3088 0.506921 9.53618C0.168974 8.72997 0 7.87338 0 7C0 6.12661 0.168974 5.27003 0.506921 4.46382C0.844869 3.69121 1.31799 2.98579 1.9094 2.39793C2.51771 1.79328 3.2105 1.323 3.98778 1.00388C4.79885 0.667959 5.66062 0.5 6.53928 0.5C7.48554 0.5 8.3811 0.70155 9.24286 1.07106C10.0708 1.44057 10.7805 1.96124 11.3888 2.63307C11.6423 2.9186 11.8451 3.27132 11.9295 3.64083C11.9802 3.79199 11.9971 4.04393 11.9971 4.27907Z"
                    fill="#484848"
                />
            </svg>

            <p className="text-13">Subscribe to Eka</p>
            <TrialIcon />
        </ProfileComponent>
    );
};

export const FrillComponent = (): JSX.Element => {
    return (
        <ProfileComponent>
            <svg
                width="14"
                height="15"
                viewBox="0 0 14 15"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
            >
                <path
                    d="M10.8594 4.78125C10.8594 5.36523 10.4023 5.79688 9.84375 5.79688C9.25977 5.79688 8.82812 5.36523 8.82812 4.78125C8.82812 4.22266 9.25977 3.76562 9.84375 3.76562C10.4023 3.76562 10.8594 4.22266 10.8594 4.78125ZM1.08398 8.4375C0.880859 8.4375 0.677734 8.33594 0.576172 8.13281C0.449219 7.95508 0.449219 7.72656 0.576172 7.52344L1.89648 5.28906C2.22656 4.73047 2.83594 4.375 3.4707 4.375H5.55273C5.62891 4.27344 5.67969 4.19727 5.75586 4.0957C7.83789 1.02344 10.9355 0.921875 12.7637 1.27734C13.0684 1.32812 13.2969 1.55664 13.3477 1.86133C13.7031 3.68945 13.6016 6.78711 10.5293 8.86914C10.4277 8.94531 10.3262 8.99609 10.25 9.07227V11.1543C10.25 11.7891 9.89453 12.3984 9.33594 12.7285L7.10156 14.0488C6.89844 14.1504 6.66992 14.1758 6.49219 14.0488C6.28906 13.9473 6.16211 13.7441 6.16211 13.5156V11.0781C5.95898 11.1797 5.75586 11.2559 5.57812 11.332C5.29883 11.459 4.94336 11.3828 4.71484 11.1543L3.41992 9.88477C3.19141 9.63086 3.11523 9.30078 3.26758 8.99609C3.31836 8.84375 3.39453 8.64062 3.49609 8.4375H1.08398ZM12.2051 2.41992C10.6055 2.19141 8.3457 2.44531 6.74609 4.78125C5.98438 5.92383 5.01953 8.03125 4.48633 9.22461L5.375 10.1133C6.56836 9.60547 8.70117 8.64062 9.84375 7.85352C12.1797 6.2793 12.4336 4.01953 12.2051 2.41992Z"
                    fill="#484848"
                />
            </svg>

            <p className="text-13">What's New</p>
        </ProfileComponent>
    );
};

export const BookTrainingComponent = (): JSX.Element => {
    return (
        <ProfileComponent>
            <svg
                width="12"
                height="13"
                viewBox="0 0 12 13"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
            >
                <path
                    d="M10.0625 0.9375C10.9512 0.9375 11.6875 1.67383 11.6875 2.5625V10.6875C11.6875 11.6016 10.9512 12.3125 10.0625 12.3125H1.9375C1.02344 12.3125 0.3125 11.6016 0.3125 10.6875V2.5625C0.3125 1.67383 1.02344 0.9375 1.9375 0.9375H10.0625ZM10.4688 10.6875V2.5625C10.4688 2.35938 10.2656 2.15625 10.0625 2.15625H1.9375C1.70898 2.15625 1.53125 2.35938 1.53125 2.5625V10.6875C1.53125 10.916 1.70898 11.0938 1.9375 11.0938H10.0625C10.2656 11.0938 10.4688 10.916 10.4688 10.6875ZM7.01562 8.65625C7.3457 8.65625 7.625 8.93555 7.625 9.26562C7.625 9.62109 7.3457 9.875 7.01562 9.875H4.98438C4.62891 9.875 4.375 9.62109 4.375 9.26562C4.375 8.93555 4.62891 8.65625 4.98438 8.65625H5.39062V7.03125H5.1875C4.83203 7.03125 4.57812 6.77734 4.57812 6.42188C4.57812 6.0918 4.83203 5.8125 5.1875 5.8125H6C6.33008 5.8125 6.60938 6.0918 6.60938 6.42188V8.65625H7.01562ZM6 5C5.54297 5 5.1875 4.64453 5.1875 4.1875C5.1875 3.75586 5.54297 3.375 6 3.375C6.43164 3.375 6.8125 3.75586 6.8125 4.1875C6.8125 4.64453 6.43164 5 6 5Z"
                    fill="#484848"
                />
            </svg>

            <p className="text-13">Request Training</p>
        </ProfileComponent>
    );
};

export const SettingsComponent = (): JSX.Element => {
    return (
        <ProfileComponent>
            <svg
                width="14"
                height="14"
                viewBox="0 0 14 14"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
            >
                <path
                    d="M4.5625 6.625C4.5625 5.2793 5.62891 4.1875 7 4.1875C8.3457 4.1875 9.4375 5.2793 9.4375 6.625C9.4375 7.9707 8.3457 9.0625 7 9.0625C5.62891 9.0625 4.5625 7.9707 4.5625 6.625ZM7 5.40625C6.31445 5.40625 5.78125 5.96484 5.78125 6.625C5.78125 7.31055 6.31445 7.84375 7 7.84375C7.66016 7.84375 8.21875 7.31055 8.21875 6.625C8.21875 5.96484 7.66016 5.40625 7 5.40625ZM7.93945 0.125C8.49805 0.125 8.98047 0.53125 9.10742 1.06445L9.31055 1.90234C9.53906 2.00391 9.74219 2.13086 9.91992 2.25781L10.7578 2.00391C11.291 1.85156 11.875 2.08008 12.1543 2.5625L13.0938 4.21289C13.373 4.69531 13.2715 5.30469 12.8652 5.68555L12.2559 6.26953C12.2559 6.39648 12.2812 6.52344 12.2812 6.625C12.2812 6.75195 12.2559 6.87891 12.2559 6.98047L12.8652 7.58984C13.2715 7.9707 13.373 8.58008 13.0938 9.0625L12.1543 10.7129C11.875 11.1953 11.291 11.4238 10.7578 11.2715L9.91992 11.0176C9.74219 11.1445 9.53906 11.2715 9.31055 11.373L9.10742 12.2109C8.98047 12.7441 8.49805 13.125 7.93945 13.125H6.03516C5.47656 13.125 4.99414 12.7441 4.86719 12.2109L4.66406 11.373C4.43555 11.2715 4.23242 11.1445 4.05469 11.0176L3.2168 11.2715C2.68359 11.4238 2.09961 11.1953 1.82031 10.7129L0.880859 9.0625C0.601562 8.58008 0.703125 7.9707 1.10938 7.58984L1.71875 6.98047C1.71875 6.87891 1.71875 6.75195 1.71875 6.625C1.71875 6.52344 1.71875 6.39648 1.71875 6.26953L1.10938 5.68555C0.703125 5.30469 0.601562 4.69531 0.880859 4.21289L1.82031 2.5625C2.09961 2.08008 2.68359 1.85156 3.2168 2.00391L4.05469 2.25781C4.23242 2.13086 4.43555 2.00391 4.66406 1.90234L4.86719 1.06445C4.99414 0.53125 5.47656 0.125 6.03516 0.125H7.93945ZM5.70508 2.76562L5.42578 2.89258C5.0957 3.01953 4.79102 3.19727 4.53711 3.40039L4.2832 3.60352L2.88672 3.17188L1.94727 4.82227L2.98828 5.8125L2.96289 6.11719C2.9375 6.29492 2.9375 6.47266 2.9375 6.625C2.9375 6.80273 2.9375 6.98047 2.96289 7.1582L2.98828 7.46289L1.94727 8.45312L2.88672 10.1035L4.2832 9.67188L4.53711 9.875C4.79102 10.0781 5.0957 10.2559 5.42578 10.3828L5.70508 10.5098L6.03516 11.9062H7.93945L8.26953 10.5098L8.54883 10.3828C8.87891 10.2559 9.18359 10.0781 9.4375 9.875L9.69141 9.67188L11.0879 10.1035L12.0273 8.45312L10.9863 7.46289L11.0117 7.1582C11.0371 6.98047 11.0625 6.80273 11.0625 6.625C11.0625 6.47266 11.0371 6.29492 11.0117 6.11719L10.9863 5.8125L12.0273 4.82227L11.0879 3.17188L9.69141 3.60352L9.4375 3.40039C9.18359 3.19727 8.87891 3.01953 8.54883 2.89258L8.26953 2.76562L7.93945 1.34375H6.03516L5.70508 2.76562Z"
                    fill="#484848"
                />
            </svg>

            <p className="text-13">Settings & preferences</p>
        </ProfileComponent>
    );
};

export const DownloadPtDBComponent = (): JSX.Element => {
    return (
        <ProfileComponent>
            {downloadIcon('w-14 h-14 fill-darwin-accent-bright-blue-800')}
            <p className="text-13 text-darwin-accent-bright-blue-800">Download patient database</p>
        </ProfileComponent>
    );
};

export const KhataComponent = (): JSX.Element => {
    const khataCredits = useSelector(getKhataCredits);
    return (
        <ProfileComponent isNew={typeof khataCredits !== 'number'}>
            <div className="w-full flex items-center justify-between">
                <div className="flex items-center space-x-8">
                    <svg
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <path
                            fill-rule="evenodd"
                            clip-rule="evenodd"
                            d="M8.03122 2.74858C5.1112 2.74858 2.74916 5.10254 2.74916 8C2.74916 10.8975 5.1112 13.2514 8.03122 13.2514C10.3332 13.2514 12.2904 11.7875 13.0142 9.74696C12.8241 9.88866 12.6182 10.0509 12.3975 10.2248C12.2727 10.3232 12.1432 10.4253 12.009 10.5295C11.5588 10.8794 11.0585 11.252 10.6022 11.4909C10.0494 11.7803 9.39978 11.9248 8.7666 11.753C8.2107 11.6022 7.74939 11.2301 7.41994 10.667C6.99823 10.8928 6.49597 10.9781 5.96176 10.7934C5.52268 10.6415 5.17287 10.2771 5.04724 9.82211C4.91364 9.3382 5.04805 8.81162 5.46191 8.39795C5.97042 7.88967 6.69391 7.90078 7.25326 8.21478C7.4031 8.2989 7.54413 8.40457 7.67395 8.52986C7.79368 8.28279 7.88849 8.02394 7.95232 7.78184C7.97162 7.70866 7.98727 7.64011 7.99979 7.57649H5.82665C5.4817 7.57649 5.20207 7.29698 5.20207 6.9522C5.20207 6.60741 5.4817 6.3279 5.82665 6.3279H7.88536C7.87167 6.26648 7.85718 6.20441 7.84182 6.14243C7.80879 6.00911 7.77509 5.89086 7.74184 5.79436H5.65441C5.30946 5.79436 5.02982 5.51486 5.02982 5.17007C5.02982 4.82528 5.30946 4.54578 5.65441 4.54578H10.542C10.887 4.54578 11.1666 4.82528 11.1666 5.17007C11.1666 5.51486 10.887 5.79436 10.542 5.79436H9.04232C9.04641 5.81042 9.05042 5.8264 9.05436 5.84229C9.09447 6.00419 9.13072 6.17348 9.16176 6.3279H10.5422C10.8871 6.3279 11.1667 6.60741 11.1667 6.9522C11.1667 7.29698 10.8871 7.57649 10.5422 7.57649H9.26531C9.24216 7.75209 9.20524 7.92937 9.16024 8.10003C9.04318 8.54398 8.84973 9.02461 8.59217 9.45734C8.52961 9.56245 8.4618 9.66717 8.38869 9.76946C8.4004 9.80993 8.41147 9.85087 8.42189 9.89239C8.62751 10.3231 8.87778 10.4894 9.09375 10.548C9.33785 10.6142 9.65929 10.5751 10.0226 10.3848C10.3679 10.2041 10.7879 9.89703 11.2423 9.54382C11.3571 9.45459 11.4747 9.36184 11.593 9.26867C11.9199 9.01106 12.2508 8.7503 12.5355 8.55238C12.7298 8.4173 12.9361 8.28723 13.1324 8.20179C13.231 8.15886 13.351 8.1165 13.4819 8.09813C13.6123 8.07984 13.7956 8.07971 13.9819 8.1703C14.4075 8.37726 14.4512 8.82062 14.4469 9.03833C14.442 9.2918 14.3737 9.59421 14.2692 9.93091L14.2688 9.93221C13.4417 12.5795 10.9614 14.5 8.03122 14.5C4.42695 14.5 1.5 11.5926 1.5 8C1.5 4.40734 4.42695 1.5 8.03122 1.5C11.1159 1.5 13.698 3.64987 14.3839 6.51088C14.4643 6.84635 14.223 7.16316 13.8813 7.2108C13.5397 7.25844 13.2278 7.01844 13.1397 6.68497C12.5436 4.4295 10.4807 2.74858 8.03122 2.74858ZM6.89636 9.52726C6.80915 9.41977 6.71965 9.34726 6.64155 9.30342C6.44962 9.19567 6.3678 9.25825 6.34521 9.28083C6.2801 9.34591 6.25923 9.39522 6.25201 9.42092C6.24475 9.44682 6.24556 9.46882 6.25139 9.48995C6.26556 9.54126 6.31251 9.59345 6.37022 9.61341C6.54192 9.67277 6.70307 9.651 6.89289 9.52949L6.89636 9.52726ZM13.1973 8.987C13.1973 8.987 13.1978 8.98953 13.198 8.99558C13.1973 8.99019 13.1973 8.987 13.1973 8.987Z"
                            fill="#484848"
                        />
                    </svg>
                    <div className="flex items-center space-x-6">
                        <p className="text-13">My Eka Credits</p>
                        {/* <Tag className="border-none text-12 text-darwin-status-warning-100 bg-darwin-status-warning-500 font-700 rounded-24 py-3 px-6">
                            Offer
                        </Tag> */}
                    </div>
                </div>

                {typeof khataCredits === 'number' &&
                    khataCredits !== undefined &&
                    !isNaN(khataCredits) && (
                        <p
                            className={`text-13 text-white ${
                                khataCredits <= 20
                                    ? 'bg-darwin-status-warning-500'
                                    : 'bg-darwin-green-primary'
                            } px-4 py-2 rounded-4 mr-8`}
                        >
                            {khataCredits}
                        </p>
                    )}
            </div>
        </ProfileComponent>
    );
};

export const SupportTicketsComponent = (): JSX.Element => {
    return (
        <ProfileComponent isNew>
            <svg
                width="16"
                height="10"
                viewBox="0 0 16 10"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
            >
                <path
                    d="M2.3125 0.25H13.6875C14.5762 0.25 15.3125 0.986328 15.3125 1.875V3.42383C15.3125 3.67773 15.1348 3.90625 14.9062 3.98242C14.4238 4.16016 14.0938 4.61719 14.0938 5.125C14.0938 5.6582 14.4238 6.11523 14.9062 6.29297C15.1348 6.36914 15.3125 6.59766 15.3125 6.85156V8.375C15.3125 9.28906 14.5762 10 13.6875 10H2.3125C1.39844 10 0.6875 9.28906 0.6875 8.375V6.85156C0.6875 6.59766 0.839844 6.36914 1.09375 6.29297C1.55078 6.11523 1.90625 5.6582 1.90625 5.125C1.90625 4.61719 1.55078 4.16016 1.09375 3.98242C0.839844 3.90625 0.6875 3.67773 0.6875 3.42383V1.875C0.6875 0.986328 1.39844 0.25 2.3125 0.25ZM1.90625 1.875V3.01758C2.61719 3.44922 3.125 4.23633 3.125 5.125C3.125 6.03906 2.61719 6.82617 1.90625 7.25781V8.375C1.90625 8.60352 2.08398 8.78125 2.3125 8.78125H13.6875C13.8906 8.78125 14.0938 8.60352 14.0938 8.375V7.25781C13.3574 6.82617 12.875 6.03906 12.875 5.125C12.875 4.23633 13.3574 3.44922 14.0938 3.01758V1.875C14.0938 1.67188 13.8906 1.46875 13.6875 1.46875H2.3125C2.08398 1.46875 1.90625 1.67188 1.90625 1.875ZM10.8438 6.34375V3.90625H5.15625V6.34375H10.8438ZM3.9375 3.5C3.9375 3.06836 4.29297 2.6875 4.75 2.6875H11.25C11.6816 2.6875 12.0625 3.06836 12.0625 3.5V6.75C12.0625 7.20703 11.6816 7.5625 11.25 7.5625H4.75C4.29297 7.5625 3.9375 7.20703 3.9375 6.75V3.5Z"
                    fill="#484848"
                />
            </svg>

            <p className="text-13">My Support Tickets</p>
        </ProfileComponent>
    );
};

export const SuccessManagerComponent: React.FC<{ hasSM: boolean }> = ({ hasSM }): JSX.Element => {
    return (
        <ProfileComponent>
            {SuccessManagerIcon('w-16 h-12 relative right-1')}
            <p className="text-13 text-darwin-accent-bright-blue-800">
                {hasSM ? 'Success Manager' : 'Schedule training'}
            </p>
        </ProfileComponent>
    );
};

export const TroubleshootMySystemComponent = (): JSX.Element => {
    return (
        <ProfileComponent>
            <svg
                className="w-14 h-14 fill-darwin-accent-bright-blue-800 relative top-1"
                viewBox="0 0 16 15"
                xmlns="http://www.w3.org/2000/svg"
            >
                <path d="M1.875 0.75H14.125C15.082 0.75 15.875 1.54297 15.875 2.5V10.375C15.875 11.3594 15.082 12.125 14.125 12.125H9.3125L9.58594 13H11.5C11.9648 13 12.375 13.4102 12.375 13.875C12.375 14.3672 11.9648 14.75 11.5 14.75H4.5C4.00781 14.75 3.625 14.3672 3.625 13.875C3.625 13.4102 4.00781 13 4.5 13H6.38672L6.6875 12.125H1.875C0.890625 12.125 0.125 11.3594 0.125 10.375V2.5C0.125 1.54297 0.890625 0.75 1.875 0.75ZM14.125 2.5H1.875V8.625H14.125V2.5Z" />
            </svg>

            <p className="text-13 text-darwin-accent-bright-blue-800">Troubleshoot My System</p>
        </ProfileComponent>
    );
};
